import { LinkProps } from '@mui/material/Link/Link';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowModel,
  GridTreeNodeWithRender,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import moment from 'moment/moment';
import { ReactNode, useCallback } from 'react';

import {
  AbsenceEntry,
  AbsenceSupersededReason,
  AbsenceType,
} from '~/data/absences/types';
import { useApplicationConfig } from '~/data/applicationConfig';
import { AbsenceStatusCellProps } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/components/AbsenceStatusCell';
import { AbsenceTableRowCommon } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/data';
import { getAttachmentUrl } from '~/utils/helper';

type PropsType = {
  employeeId: number;
  absenceType: AbsenceType;
  canApproveAbsence: boolean;
  canEditAbsences: boolean;
};

type FieldRemoveCondition = [string, boolean];

type GetColumnsWithCellRenderingProps<
  TRow extends AbsenceTableRowCommon & GridValidRowModel,
  V extends GridValidRowModel = TRow,
  F = unknown,
> = {
  columnConfigs: GridColDef<TRow, V, F>[];
  renderActionsComponent?: (
    params: GridRenderCellParams<TRow, TRow, F>,
  ) => ReactNode;
  renderStatusComponent?: (
    params: AbsenceStatusCellProps,
    row: GridRowModel<TRow>,
  ) => ReactNode;
  renderCreatedComponent?: (
    params: {
      created?: Date;
      name?: string;
      absenceEntry: AbsenceEntry;
    },
    row: GridRowModel<TRow>,
  ) => ReactNode;
  renderAttachmentComponent?: (params: LinkProps) => ReactNode;
};

export type UseAbsenceTableUtilsOutput<
  TRow extends AbsenceTableRowCommon,
  V extends GridValidRowModel,
  F,
> = {
  filterAvailableColumns: (
    columnConfigs: GridColDef<TRow>[],
  ) => GridColDef<TRow>[];
  getColumnsWithCellRendering: (
    props: GetColumnsWithCellRenderingProps<TRow, V, F>,
  ) => GridColDef<TRow, V, F>[];
};

export const useAbsenceTableUtils = <
  TRow extends AbsenceTableRowCommon & GridValidRowModel,
  V extends GridValidRowModel,
  F,
>(
  props: PropsType,
): UseAbsenceTableUtilsOutput<TRow, V, F> => {
  const { employeeId, absenceType, canApproveAbsence, canEditAbsences } = props;
  const { applicationConfig } = useApplicationConfig();

  const filterColumnsByFieldName = useCallback(
    (
      columnConfigs: GridColDef<TRow>[],
      removeFields: (string | FieldRemoveCondition)[],
    ): GridColDef<TRow>[] => {
      const rf = removeFields.reduce((acc, field) => {
        if (typeof field === 'string') {
          return [...acc, field];
        }

        const [fieldName, condition] = field;

        return condition ? [...acc, fieldName] : acc;
      }, [] as string[]);

      return columnConfigs.filter((cc) => !rf.includes(cc.field));
    },
    [],
  );

  const filterColumnsByType = useCallback(
    (columnConfigs: GridColDef<TRow>[]): GridColDef<TRow>[] => {
      if (absenceType === AbsenceType.LONG) {
        return filterColumnsByFieldName(columnConfigs, [
          'year',
          'days',
          'hours',
          'plannedWorkingHours',
        ]);
      }

      return filterColumnsByFieldName(columnConfigs, [
        [
          'year',
          !applicationConfig?.fComConfig?.absConfig
            ?.fComEnableYearlyVacationBalance,
        ],
        [
          'hours',
          !applicationConfig?.fComConfig?.absConfig?.fComEnableAbsenceHours,
        ],
        [
          'plannedWorkingHours',
          !applicationConfig?.fComConfig?.absConfig?.fComEnableAbsenceHours,
        ],
        ['actions', !canEditAbsences],
      ]);
    },
    [filterColumnsByFieldName, applicationConfig, absenceType, canEditAbsences],
  );

  const filterColumnsByUserPermissions = useCallback(
    (columnConfigs: GridColDef<TRow>[]): GridColDef<TRow>[] => {
      const userCanEdit = true;

      return filterColumnsByFieldName(columnConfigs, [
        ['actions', !userCanEdit],
      ]);
    },
    [filterColumnsByFieldName],
  );

  const getColumnsWithCellRendering = useCallback(
    (renderProps: GetColumnsWithCellRenderingProps<TRow, V, F>) => {
      const {
        columnConfigs,
        renderStatusComponent,
        renderCreatedComponent,
        renderAttachmentComponent,
        renderActionsComponent,
      } = renderProps;

      return columnConfigs.map((cc): GridColDef<TRow> => {
        if (['startDate', 'endDate'].includes(cc.field)) {
          return {
            ...cc,
            renderCell: ({ value }) =>
              !!value ? moment(value).format('L') : '',
          };
        }

        if (cc.field === 'attachment' && renderAttachmentComponent) {
          return {
            ...cc,
            renderCell: ({ row }) => {
              const { attachment } = row.meta;
              const attachmentId = attachment?.id ?? -1;
              const attachmentName = attachment?.name ?? '';
              if (attachmentId > -1) {
                if (attachment?.isArchived) {
                  return attachmentName;
                } else {
                  return renderAttachmentComponent({
                    href: getAttachmentUrl(`${employeeId}`, attachmentId),
                    children: attachmentName,
                  });
                }
              }
              return '';
            },
          };
        }

        if (cc.field === 'status' && renderStatusComponent) {
          return {
            ...cc,
            renderCell: ({ row, value }) =>
              renderStatusComponent(
                { absenceEntry: row.meta, status: value, canApproveAbsence },
                row,
              ),
          };
        }

        if (cc.field === 'created' && renderCreatedComponent) {
          return {
            ...cc,
            renderCell: ({ row, value }) => {
              const {
                meta: { supersededReason, supersededName, enteredUserName },
              } = row;
              return renderCreatedComponent(
                {
                  absenceEntry: row.meta,
                  created: value,
                  name:
                    supersededReason === AbsenceSupersededReason.DELETE
                      ? supersededName
                      : enteredUserName,
                },
                row,
              );
            },
          };
        }

        if (cc.field === 'actions' && renderActionsComponent) {
          return {
            ...cc,
            renderCell: (
              params: GridRenderCellParams<
                TRow,
                TRow,
                F,
                GridTreeNodeWithRender
              >,
            ) => renderActionsComponent(params),
          };
        }

        if (['year', 'hours', 'plannedWorkingHours'].includes(cc.field)) {
          return {
            ...cc,
            renderCell: ({ value }) => (value === 0 ? '-' : value),
          };
        }

        return cc;
      });
    },
    [employeeId, canApproveAbsence],
  );

  const filterAvailableColumns = useCallback(
    (columnConfigs: GridColDef<TRow>[]) => {
      return filterColumnsByUserPermissions(filterColumnsByType(columnConfigs));
    },
    [filterColumnsByUserPermissions, filterColumnsByType],
  );

  return {
    filterAvailableColumns,
    getColumnsWithCellRendering,
  };
};
